import { useEffect, useRef, useState } from "react"

import { DateTime } from "luxon"
import { useForm } from "react-hook-form"

import { useQueryClient } from "@tanstack/react-query"

import Avatar from "../../user/Avatar"
import useNotes from "../../../data/useNotes"
import { TextInput } from "../Inputs/TextInput"
import ConfirmModal from "../Confirm/ConfirmModal"
import { ButtonInput } from "../Inputs/ButtonInput"
import { useAuth } from "../../../contexts/AuthContext"
import ContactFromId from "../../contacts/ContactFromId"
import formatDateTime from "../../../utils/formatDateTime"
import { INewNote, INote, ResourceType } from "../../../models/Note"
import useDefaultCRUDHandlers from "../../../hooks/useDefaultCRUDHandlers"

interface INotesOutputProps {
  resourceType: ResourceType;
  resourceId: string;
  date?: string;
  refetchQuery?: any[];
  shouldScroll?: boolean;
  classes?: string;
  compact?: boolean;
  notes?: INote[];
};

export default function NotesOutput({
  resourceType,
  resourceId,
  date = DateTime.now().toISODate()!,
  refetchQuery,
  shouldScroll = true,
  classes = "",
  compact = false,
  notes: forcedNotes,
}: INotesOutputProps) {
  const { register, handleSubmit, reset } = useForm<any>();
  const { saveHandlers, deleteHandlers } = useDefaultCRUDHandlers("Note");
  const queryClient = useQueryClient();
  const { claims, userId } = useAuth();

  const { useNotesByResource, create, remove } = useNotes();

  const { data } = useNotesByResource(resourceId);
  const [notes, setNotes] = useState<INote[]>(forcedNotes ?? []);

  // const [isAdmin, setIsAdmin] = useState(false);
  const [deleteId, setDeleteId] = useState<string | undefined>(undefined);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    const hasForcedNotes = forcedNotes !== undefined;
    if (hasForcedNotes) {
      setNotes(forcedNotes);
      return;
    }

    if (data?.success && data.data) {
      setNotes(data.data.filter((note: INote) => note.resourceType === resourceType));
      return;
    }

    setNotes([]);
  }, [forcedNotes, data?.success, data?.data, resourceType]);

  const onSubmit = (data: any) => {
    const newNote: INewNote = {
      message: data.message,
      resourceId: resourceId,
      resourceType: resourceType,
      date: date
    };

    create.mutate(newNote, {
      onSuccess: () => {
        saveHandlers.onSuccess();
        invalidateCache();
        reset({ message: "" });
      },
      onError: saveHandlers.onError
    });
  }

  const messagesEndRef = useRef<null | HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    if (shouldScroll) {
      setTimeout(() => {
        scrollToBottom();
      }, 500);
    }
  }, [notes, shouldScroll])

  const deleteNote = () => {
    setDeleteLoading(true);
    if (deleteId) {
      remove.mutate(deleteId, {
        onSuccess: () => {
          setDeleteOpen(false);
          deleteHandlers.onSuccess();
          invalidateCache();
        },
        onError: () => {
          deleteHandlers.onError();
        },
        onSettled: () => {
          setDeleteLoading(false);
        }
      });
    }
  };

  const invalidateCache = () => {
    if (refetchQuery && refetchQuery.length > 0) {
      console.log("invalidating notes with query: ", refetchQuery);
      queryClient.invalidateQueries(refetchQuery);
    } else {
      queryClient.invalidateQueries(["notes", resourceId]);
    }
  }

  return (
    <>
      <ConfirmModal open={deleteOpen} setOpen={setDeleteOpen}
        title={"Remove note"} message={`Are you sure you want to remove this note?`} confirmButtonText={"Remove note"}
        onConfirm={deleteNote} isLoading={deleteLoading}
      />
      <div className={`max-h-[70vh] overflow-auto ${compact ? "px-0" : "px-4"} ${classes}`}>
        <div className={`${compact ? "mt-1 space-y-1" : "mt-2 space-y-2"} divide-y`}>
          {notes.map((note, i) => {
            return (
              <div key={i} className={`flex justify-between break-words whitespace-pre-wrap ${compact ? "pt-1.5" : "pt-2"}`}>
                <div className="flex justify-start">
                  <div>
                    <Avatar classes={`${compact ? "w-7 h-7" : "w-8 h-8"} mr-2`} textClasses={`${compact ? "!text-sm" : ""}`} userId={note.createdBy} />
                  </div>
                  <div className="flex flex-col">
                    <span className={compact ? "text-xs" : "text-sm"}>
                      <span className="font-semibold"><ContactFromId contactId={note.createdBy} /></span> - {formatDateTime(note.created, DateTime.DATETIME_SHORT)}
                    </span>
                    <p className={compact ? "mt-1 text-sm" : "mt-2"}>{note.message}</p>
                  </div>
                </div>
                <div>
                  {(userId === note.createdBy || claims?.isAdmin) && (
                    <ButtonInput
                      label={"X"}
                      isSubmit={false}
                      onClick={() => { setDeleteOpen(true); setDeleteId(note.id); }}
                      classes={`${compact ? "!p-0.5 !px-1.5 !text-xs" : "!p-1 !px-2"} !bg-red-500 hover:!bg-red-700`}
                    />
                  )}
                </div>
              </div>
            )
          })}
        </div>
        <form onSubmit={handleSubmit(onSubmit)} className={compact ? "mt-3 flex items-end gap-x-2" : "mt-6"}>
          <TextInput
            label={"New Note"}
            register={register}
            registerName={"message"}
            cols={compact ? "flex-1" : "sm:col-span-1"}
            size={compact ? "sm" : "md"}
          />
          <ButtonInput
            label={"Save"}
            isSubmit={true}
            onClick={undefined}
            // classes={compact ? "!py-1 !px-3 h-[34px] mb-[1px]" : "mt-2"}
            classes={compact ? "mb-[1px]" : "mt-2"}
            size={compact ? "sm" : "md"}
          />
        </form>
        <div ref={messagesEndRef} />
      </div>
    </>
  )
}